/**
 * Générateur, Attendu 181, « Il construit un agrandissement ou une réduction
 * d'une figure donnée. »
 * Niveau : 4ème | Thème #9 Transformations | Grandeurs et mesures.
 *
 * PLAFOND (attendus de fin de 4e, Eduscol, `.claude/reference/eduscol-attendus-fin-4e.md`
 * l. 337, rubrique « Comprendre l'effet de quelques transformations sur les
 * figures géométriques ») : « Il construit un agrandissement ou une réduction
 * d'une figure donnée. » SANS centre. Aucun des PDF du cours du professeur ne
 * contient de construction d'un agrandissement : 181 n'a pas de cours.
 *
 * L'HOMOTHÉTIE ET LE CENTRE O SONT DE 3e (cadrage 2026-09-25, sources) :
 * repères annuels 2019 p. 12 (4e = translation seule ; 3e « transforment une
 * figure par rotation et par homothétie (de rapport positif ou négatif) » ;
 * « les définitions ponctuelles d'une translation, d'une rotation et d'une
 * homothétie ne figurent pas au programme ») ; attendus de fin de 3e p. 7-8
 * (« deux homothéties de centre O et de rapports 3 et −0,5 ») ; BO 2025 cycle
 * 4 : « homothétie » et « agrandissement » absents des 1 438 lignes.
 *
 * Historique : cinq cartes de juillet 2026 (deux constructions par homothétie
 * de centre O, les coordonnées de l'image d'un point, l'effet sur l'aire, un
 * QCM « agrandissement, réduction ou même taille »), jamais revues, fermées.
 *
 * ── Revue 2026-09-25 (vague 7, décisions du coordinateur,
 * `scratchpad/vague7/DECISIONS_180-181.md`, cadrage
 * `.claude/agent-memory/programme-maths/project_cadrage_180-181.md`, audit
 * `audit_180-181.md`) ───────────────────────────────────────────────────────
 * SORTIES (et leurs raisons) :
 *   - « Construire un agrandissement » et « Construire une réduction »
 *     (construire_agrandissement, construire_reduction) : homothétie de centre
 *     O (3e) ; la figure n'était jamais dessinée (pas de décor) ; toujours en
 *     haut à droite de O (100 %) ; deux onglets d'un même format.
 *   - « Coordonnées de l'image » (coord_image_texte_trou) : l'image d'un POINT
 *     par une transformation de centre O, la définition ponctuelle de
 *     l'homothétie (3e) ; un point n'est pas « une figure donnée » ; image hors
 *     du repère dans 6,4 %.
 *   - « Effet sur l'aire » (facteur_aire_saisie) : la ligne de 180 (l. 334),
 *     pur rappel k → k² ; « Tu viens de construire » sans construction (49 %).
 *   - « Agrandissement ou réduction ? » (reconnaitre_qcm) : se lisait à vue,
 *     « même taille » jamais la clé (100 %), parenthèses qui soufflaient.
 * DEUX CARTES, ni rappel brouillon ni calculatrice (lecture et placement).
 *   1. « Construire l'agrandissement ou la réduction » (construire_placer_points_2d,
 *      onglet « Repère ») : une figure DESSINÉE sur un quadrillage 12 × 12
 *      (repere.quadrillage : true, contenu.decor, comme 187 « Construire
 *      l'image d'une figure » et utils/repereDecor.js) : rectangle (4 sommets),
 *      « L » (3 sommets, deux segments perpendiculaires) ou triangle rectangle
 *      (3 sommets, une diagonale) ; côtés horizontaux et verticaux ; rapport
 *      k ∈ {2, 3, 1/2} (1/2 impose des côtés pairs) ; « On garde le sommet A :
 *      A' = A. Place les autres sommets B', C' (D') … » : l'image est unique
 *      (même orientation, même sens de déploiement, B' = A + k × (B − A)), tous
 *      les sommets image sur des nœuds entiers, DANS la grille et jamais sur
 *      ses bords (tout dans [1 ; 11]) ; le sommet gardé varie (coin bas
 *      gauche, haut droit…, par le choix du sommet et l'orientation) et la
 *      figure occupe tout le repère au fil des tirages (mesuré dans
 *      gen181.self.test.js : les quatre quarts). masquerCoordonnees : true
 *      (on compte des carreaux). PAS de contenu.relier, points seuls : relier
 *      ne relie que les points à placer, le sommet gardé A n'en est pas un,
 *      la figure image (A B' C' D') ne peut donc pas être fermée par
 *      repereDecor (« sinon points seuls », décision) ; une évolution de
 *      repereDecor (un point de dejaPlaces admis dans relier) le permettrait.
 *   2. « La bonne construction » (reconnaitre_qcm_unique, QCM d'images, LE QCM
 *      de l'attendu) : la figure bleue dessinée et nommée dans l'énoncé
 *      (imageUrl), quatre dessins en imageData par choix (même quadrillage,
 *      même échelle, la figure bleue rappelée en clair et une figure orange
 *      construite dessus) ; erreurs nommées : additif (chaque côté allongé,
 *      ou raccourci, de k carreaux au lieu de multiplié : DEPP 2de 2025 q.13,
 *      30,7 %, PRINCIPALE, toujours présente), un_seul_cote (une seule
 *      dimension multipliée), coefficient_inverse (réduite au lieu d'agrandie,
 *      ou un autre rapport) ; sommet_deplace (même taille, translatée, le
 *      sommet A non gardé) en REMPLACEMENT quand coefficient_inverse ne tient
 *      pas sur le quadrillage ou ne tombe pas sur des nœuds, un hors-support au
 *      plus ; quatre dessins deux à deux distincts et distincts de la figure
 *      (throw sinon) ; chaque SVG porte data-figure (les sommets du dessin
 *      orange) et l'énoncé data-depart, pour le solveur test-side. MESURÉ sur
 *      2 000 tirages (gen181.self.test.js, aire du cadre de chaque dessin) :
 *      la clé est le plus grand dessin dans 38 % (rapport 3 : 82 %, rapport 2 :
 *      30 %, rapport 1/2 : 0 %) et le plus petit dans 3 % ; hors-support dans
 *      3 % des tirages ; rapports 34 / 34 / 33 %, formes 33 / 34 / 33 %. Le
 *      rapport et la forme sont tirés une fois, puis jusqu'à quatre tirages de
 *      côtés et d'orientation cherchent une grille à trois erreurs de support
 *      avant d'accepter le hors-support (sinon il sortait dans 41 % des tirages,
 *      surtout en réduction où « ×2 » ne tient pas sur le quadrillage).
 * RAPPORTS : en KaTeX (tex / fracTex) ou en mots (« dont toutes les longueurs
 * sont doublées / triplées / réduites de moitié ») une fois sur deux ; jamais
 * « k » en clair.
 * SACRIFIÉ : aucun exemple du cours (pas de cours) ; le rectangle de 2 × 1
 * carreaux agrandi de rapport 3 (image 6 × 3), proposé par le cadrage pour la
 * future fiche, est exclu des deux cartes.
 * Solveurs test-side : tests/solvers/transformations-4e.js (la construction
 * relit le décor et le rapport dans le texte ; le QCM lit data-depart de
 * l'énoncé et data-figure des choix).
 */

import { randInt, randomChoice, tex, fracTex, makeQcm, basePayload } from '../../../utils';
import { svgQuadrillage, imageEnonce, BLEU, ORANGE } from './transformations';

const ATTENDU_ID = 181;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_CONS = 'Construire l\'agrandissement ou la réduction';
const L_RECON = 'La bonne construction';

const GMAX = 12;
const MIN = 1;                                  // rien sur les bords : tout dans [1 ; 11]
const MAX = GMAX - 1;
const NOMS = ['A', 'B', 'C', 'D'];
export const RAPPORTS = [{ n: 2, d: 1 }, { n: 3, d: 1 }, { n: 1, d: 2 }];
export const FORMES = ['rectangle', 'L', 'triangle'];
const kStr = ({ n, d }) => (d === 1 ? String(n) : `${n}/${d}`);
const agrandit = (k) => k.d === 1;
const MOTS = { 2: 'doublées', 3: 'triplées', '1/2': 'réduites de moitié' };
const tirerEcriture = () => (randInt(0, 1) === 0 ? 'mots' : 'katex');
const rapportKatex = (k) => (agrandit(k) ? `l'agrandissement de rapport ${tex(String(k.n))}` : `la réduction de rapport ${fracTex(k.n, k.d)}`);

/** Le rectangle 2 × 1 agrandi de rapport 3 : réservé à la fiche (cadrage), jamais tiré. */
const SACRIFIE = { forme: 'rectangle', cotes: [2, 1], k: '3' };
const estSacrifie = (forme, a, b, k) => forme === SACRIFIE.forme && kStr(k) === SACRIFIE.k
    && Math.max(a, b) === Math.max(...SACRIFIE.cotes) && Math.min(a, b) === Math.min(...SACRIFIE.cotes);

// ═════════ Géométrie : la figure, son orientation, son image ═════════

const entiers = (pts) => pts.every((p) => Number.isInteger(p[0]) && Number.isInteger(p[1]));
const dansGrille = (pts) => pts.every((p) => p[0] >= MIN && p[0] <= MAX && p[1] >= MIN && p[1] <= MAX);
const cleDe = (pts) => pts.map((p) => p.join(';')).join('|');
const egal = (p, q) => p[0] === q[0] && p[1] === q[1];
const scaler = (rel, k) => rel.map((p) => [(p[0] * k.n) / k.d, (p[1] * k.n) / k.d]);
const plus = (A, rel) => rel.map((p) => [A[0] + p[0], A[1] + p[1]]);

/**
 * La figure en coordonnées locales (le coin droit en (0 ; 0), a horizontal, b
 * vertical), le sommet gardé A étant le i-ième ; les noms suivent l'ordre du
 * polygone (rectangle, triangle) ou du chemin (L : bout, coin, bout).
 * decor : les polygones du décor, en noms (un « polygone » de deux noms est un segment).
 */
export const formeLocale = (forme, a, b, i) => {
    if (forme === 'rectangle') {
        const v = [[0, 0], [a, 0], [a, b], [0, b]];
        return { pts: v.map((_, j) => v[(i + j) % 4]), decor: [['A', 'B', 'C', 'D']] };
    }
    if (forme === 'triangle') {
        const v = [[0, 0], [a, 0], [0, b]];
        return { pts: v.map((_, j) => v[(i + j) % 3]), decor: [['A', 'B', 'C']] };
    }
    const chemin = [[0, b], [0, 0], [a, 0]];
    if (i === 1) return { pts: [chemin[1], chemin[0], chemin[2]], decor: [['B', 'A'], ['A', 'C']] };
    return { pts: i === 0 ? chemin : [...chemin].reverse(), decor: [['A', 'B'], ['B', 'C']] };
};

/**
 * Côtés : k = 3 → 1 à 3 (image ≤ 9) ; k = 2 → 1 à 5 sur la carte Repère, 1 à 4 au
 * QCM (un côté de 5 n'y laisse aucune place au rapport 3 du coefficient_inverse) ;
 * k = 1/2 → pairs, 4 à 10 sur la carte Repère (image ≤ 5), 4 à 8 au QCM (une
 * figure de 10 remplit le quadrillage, aucun autre dessin n'y tient). Avec des
 * côtés d'au moins 4, l'erreur additive « −2 carreaux » reste toujours possible.
 */
const tirerCotes = (k, qcm = false) => {
    for (let e = 0; e < 60; e++) {
        if (k.d === 2) return [2 * randInt(2, qcm ? 4 : 5), 2 * randInt(2, qcm ? 4 : 5)];
        const max = k.n === 3 ? 3 : (qcm ? 4 : 5);
        const a = randInt(1, max); const b = randInt(1, max);
        if (Math.max(a, b) >= 2) return [a, b];
    }
    return k.d === 2 ? [6, 4] : [3, 2];
};

/** La plage des positions de A telles que tous les points relatifs tiennent dans [1 ; 11]² ; null si aucune. */
const plage = (rels) => {
    const xs = rels.map((p) => p[0]); const ys = rels.map((p) => p[1]);
    const r = { xLo: MIN - Math.min(...xs), xHi: MAX - Math.max(...xs), yLo: MIN - Math.min(...ys), yHi: MAX - Math.max(...ys) };
    return r.xLo <= r.xHi && r.yLo <= r.yHi ? r : null;
};
const positionAuHasard = (r) => [randInt(r.xLo, r.xHi), randInt(r.yLo, r.yHi)];

/** Où est le sommet gardé dans sa figure : « bas gauche », « haut droite »… (mesure de variété). */
export const coinDe = (sommets) => {
    const A = sommets[0];
    const xs = sommets.map((p) => p[0]); const ys = sommets.map((p) => p[1]);
    return `${A[1] === Math.min(...ys) ? 'bas' : 'haut'} ${A[0] === Math.min(...xs) ? 'gauche' : 'droite'}`;
};

/** Les segments d'un décor appliqué à des points (dans l'ordre des noms). */
const segmentsDe = (decor, pts) => decor.flatMap((poly) => poly.map((nm, j) => [pts[NOMS.indexOf(nm)], pts[NOMS.indexOf(poly[(j + 1) % poly.length])]]).slice(0, poly.length === 2 ? 1 : poly.length));

/** Une figure : au plus une diagonale (le triangle), tous les autres côtés horizontaux ou verticaux. */
const cotesDroits = (decor, pts) => segmentsDe(decor, pts).filter(([p, q]) => p[0] !== q[0] && p[1] !== q[1]).length <= 1;

// ═════════ Carte 1 : « Construire l'agrandissement ou la réduction » (Repère, placer_points_2d) ═════════

/** Un tirage complet : rapport, forme, côtés, sommet gardé, orientation, position ; ou null s'il ne tient pas. */
export const construireTirage = ({ k, forme, a, b, i, sx, sy }, choisirA = positionAuHasard) => {
    if (estSacrifie(forme, a, b, k)) return null;
    const { pts, decor } = formeLocale(forme, a, b, i);
    const orientes = pts.map(([x, y]) => [sx * x, sy * y]);
    const rel = orientes.map((p) => [p[0] - orientes[0][0], p[1] - orientes[0][1]]);
    const relImg = scaler(rel, k);
    if (!entiers(relImg)) return null;
    const r = plage([...rel, ...relImg]);
    if (!r) return null;
    const A = choisirA(r);
    return { k, forme, a, b, decor, sommets: plus(A, rel), images: plus(A, relImg) };
};

export const tirerConstruction = () => {
    for (let essai = 0; essai < 400; essai++) {
        const k = randomChoice(RAPPORTS); const forme = randomChoice(FORMES);
        const [a, b] = tirerCotes(k);
        const t = construireTirage({ k, forme, a, b, i: randInt(0, forme === 'rectangle' ? 3 : 2), sx: randomChoice([1, -1]), sy: randomChoice([1, -1]) });
        if (t) return t;
    }
    // Repli déterministe, vérifié par payloadConstruire : le rectangle 3 × 2 doublé depuis son coin bas gauche.
    return construireTirage({ k: RAPPORTS[0], forme: 'rectangle', a: 3, b: 2, i: 0, sx: 1, sy: 1 }, (r) => [r.xLo + 1, r.yLo + 1]);
};

const constructionValide = (t) => t && [3, 4].includes(t.sommets.length) && t.images.length === t.sommets.length
    && entiers(t.sommets) && entiers(t.images) && dansGrille(t.sommets) && dansGrille(t.images)
    && egal(t.images[0], t.sommets[0])
    && new Set([...t.sommets, ...t.images.slice(1)].map((p) => p.join(';'))).size === 2 * t.sommets.length - 1
    && t.a >= 1 && t.b >= 1 && cotesDroits(t.decor, t.sommets)
    && RAPPORTS.some((k) => k.n === t.k.n && k.d === t.k.d) && !estSacrifie(t.forme, t.a, t.b, t.k);

const liste = (noms) => (noms.length === 2 ? `${noms[0]} et ${noms[1]}` : `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`);

export const payloadConstruire = (t, ecriture) => {
    if (!constructionValide(t)) throw new Error('gen_181 construire : tirage invalide');
    const noms = NOMS.slice(0, t.sommets.length);
    const aPlacer = noms.slice(1).map((nm) => `${nm}'`);
    const dejaPlaces = t.sommets.map((p, i) => ({ x: p[0], y: p[1], label: noms[i] }));
    const bonne = {};
    aPlacer.forEach((nm, i) => { const q = t.images[i + 1]; bonne[nm] = `${q[0]};${q[1]}`; });
    // Second chemin : depuis dejaPlaces (ce que voit l'élève) et le rapport, chaque image est A + k × (P − A).
    const lu = Object.fromEntries(dejaPlaces.map((d) => [d.label, [d.x, d.y]]));
    noms.slice(1).forEach((nm) => {
        const q = [lu.A[0] + ((lu[nm][0] - lu.A[0]) * t.k.n) / t.k.d, lu.A[1] + ((lu[nm][1] - lu.A[1]) * t.k.n) / t.k.d];
        if (bonne[`${nm}'`] !== `${q[0]};${q[1]}`) throw new Error(`gen_181 construire : cible ${nm}'`);
    });
    const nomFigure = noms.join('');
    const complement = ecriture === 'mots'
        ? `de la figure ${nomFigure} dont toutes les longueurs sont ${MOTS[kStr(t.k)]}`
        : `de ${rapportKatex(t.k)} de la figure ${nomFigure}`;
    const payload = basePayload('association',
        `<p>La figure ${nomFigure} est dessinée sur le quadrillage. On garde le sommet A : ${tex('A\' = A')}.</p>`
        + `<p><b>Place les autres sommets ${liste(aPlacer)} ${complement}.</b></p>`,
        {
            subType: 'placer_points_2d',
            // quadrillage sans axes, la figure dessinée (décision 2026-09-25) ; points seuls, sans relier (voir l'en-tête)
            repere: { xMin: 0, yMin: 0, xMax: GMAX, yMax: GMAX, xStep: 1, yStep: 1, quadrillage: true },
            pointsToPlace: aPlacer,
            dejaPlaces,
            decor: { polygones: t.decor },
            masquerCoordonnees: true,
            bonne_reponse: JSON.stringify(bonne),
        },
        opts(L_CONS));
    payload._meta.params = {
        forme: t.forme, k: kStr(t.k), cotes: [t.a, t.b], sommetGarde: coinDe(t.sommets), ecriture,
        sommets: t.sommets, images: t.images,
    };
    return payload;
};

const genConstruire = () => payloadConstruire(tirerConstruction(), tirerEcriture());

// ═════════ Carte 2 : « La bonne construction » (QCM d'images) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la figure bleue (rectangle, L ou triangle rectangle, côtés horizontaux et verticaux) est dessinée et nommée dans l\'énoncé (imageUrl) ; chaque choix est un dessin en imageData sur le MÊME quadrillage 12 × 12 à la même échelle, la figure bleue rappelée en clair et une figure orange construite dessus',
        'la clé et les trois erreurs de support partent du sommet gardé A (A\' = A), comme la construction de la carte Repère ; seul le hors-support ne le garde pas',
        'quatre dessins deux à deux distincts et distincts de la figure de départ, tous dans [1 ; 11]² (throw sinon) ; codes dans _meta.params.distracteurs ; le rapport en KaTeX ou en mots une fois sur deux',
        'sacrifié : le rectangle 2 × 1 agrandi de rapport 3 (fiche), jamais tiré',
    ],
    reconnaitre_qcm_unique: {
        support: 'rapport 2 (côtés 1 à 5), 3 (côtés 1 à 3) ou 1/2 (côtés pairs 4 à 10) ; « Sur quel dessin la figure orange est-elle l\'agrandissement de rapport 2 de la figure bleue ? »',
        cle: 'chaque sommet P\' = A + k × (P − A) : même orientation, même sens de déploiement',
        familles: {
            additif: 'chaque côté allongé de k carreaux (agrandissement) ou raccourci de 2 carreaux (réduction de rapport 1/2) au lieu d\'être multiplié : le modèle additif (DEPP 2de GT 2025 q.13 : 8 pour 6, 30,7 %), PRINCIPALE, toujours présente ; un tirage où elle coïncide avec la clé (carré 2 × 2 doublé, carré 4 × 4 réduit de moitié) est retiré',
            un_seul_cote: 'une seule dimension multipliée par k (l\'horizontale ou la verticale, tirée) : la figure étirée',
            coefficient_inverse: 'réduite au lieu d\'agrandie (rapport 1/2 pour 2, 1/3 pour 3) ou l\'inverse (2 pour 1/2), sinon un autre rapport (3 pour 2, 2 pour 3, 1/4 pour 1/2) ; la première écriture qui tombe sur des nœuds entiers',
            sommet_deplace: 'HORS SUPPORT, en remplacement seulement : la figure de même taille déplacée d\'un à deux carreaux, le sommet A non gardé ; posée quand coefficient_inverse ne tombe pas sur des nœuds ou ne tient pas sur le quadrillage ; au plus une par tirage',
        },
        supprimees: {
            meme_taille_option_texte: 'l\'ancienne option « Une figure de même taille » : jamais la clé, on répondait à la taille sans maths',
            rapport_faux_lointain: 'un rapport 4 ou 5 : une figure qui déborde du quadrillage se reconnaît sans construire',
        },
        residu: 'mesuré sur 2 000 tirages (gen181.self.test.js, aire du cadre de chaque dessin) : la clé n\'est ni toujours la plus grande ni toujours la plus petite ; en agrandissement elle est souvent la plus grande, en réduction souvent la plus petite, la grandeur du dessin ne suffit donc pas',
    },
};

const CODES = ['additif', 'un_seul_cote', 'coefficient_inverse', 'sommet_deplace'];

/**
 * Les rapports essayés pour coefficient_inverse, dans l'ordre : pour un
 * agrandissement l'inverse d'abord (réduite au lieu d'agrandie), puis un autre
 * rapport ; pour la réduction 1/2, un autre rapport de réduction d'abord (1/4,
 * 1/3 : ils tiennent toujours), puis l'agrandissement 2 (il ne tient que pour des
 * côtés de 4). Le premier qui tombe sur des nœuds ET tient sur le quadrillage.
 */
const inversesDe = (k) => (agrandit(k)
    ? [{ n: 1, d: k.n }, { n: k.n === 2 ? 3 : 2, d: 1 }]
    : [{ n: 1, d: 4 }, { n: 1, d: 3 }, { n: 2, d: 1 }]);

/**
 * Un tirage complet du QCM : la figure, la clé et trois distracteurs, tous
 * placés ; null s'il ne tient pas (le tirage est alors rejoué, jamais bouché).
 */
export const reconnaitreTirage = ({ k, forme, a, b, i, sx, sy, axe, vecteur }, choisirA = positionAuHasard) => {
    if (estSacrifie(forme, a, b, k)) return null;
    const { pts, decor } = formeLocale(forme, a, b, i);
    const orientes = pts.map(([x, y]) => [sx * x, sy * y]);
    const rel = orientes.map((p) => [p[0] - orientes[0][0], p[1] - orientes[0][1]]);
    const cle = scaler(rel, k);
    if (!entiers(cle)) return null;
    // additif : chaque côté allongé de k carreaux, ou raccourci de 2 (réduction) ; l'erreur principale, toujours présente
    const t = agrandit(k) ? k.n : -k.d;
    if (rel.some(([x, y]) => (x !== 0 && Math.abs(x) + t < 1) || (y !== 0 && Math.abs(y) + t < 1))) return null;
    const additif = rel.map(([x, y]) => [x === 0 ? 0 : Math.sign(x) * (Math.abs(x) + t), y === 0 ? 0 : Math.sign(y) * (Math.abs(y) + t)]);
    // un seul côté : une seule dimension multipliée
    const unSeul = rel.map(([x, y]) => (axe === 0 ? [(x * k.n) / k.d, y] : [x, (y * k.n) / k.d]));
    if (!entiers(unSeul)) return null;
    const base = [{ code: 'cle', rel: cle }, { code: 'additif', rel: additif }, { code: 'un_seul_cote', rel: unSeul, axe }];
    // coefficient inverse : le premier rapport qui tombe sur des nœuds et dont le dessin tient avec les autres
    let candidats = null; let r = null;
    for (const kk of inversesDe(k)) {
        const s = scaler(rel, kk);
        if (!entiers(s)) continue;
        const essai = [...base, { code: 'coefficient_inverse', rel: s, rapport: kStr(kk) }];
        const plageEssai = plage([...rel, ...essai.flatMap((c) => c.rel)]);
        if (plageEssai) { candidats = essai; r = plageEssai; break; }
    }
    // Sinon le hors-support en remplacement, un au plus : la figure de même taille, déplacée.
    if (!candidats) {
        candidats = [...base, { code: 'sommet_deplace', rel: rel.map(([x, y]) => [x + vecteur[0], y + vecteur[1]]), vecteur }];
        r = plage([...rel, ...candidats.flatMap((c) => c.rel)]);
    }
    if (!r) return null;
    // Deux à deux distincts, et aucun n'est la figure elle-même.
    if (new Set([cleDe(rel), ...candidats.map((c) => cleDe(c.rel))]).size !== 5) return null;
    const A = choisirA(r);
    return { k, forme, a, b, decor, sommets: plus(A, rel), candidats: candidats.map((c) => ({ ...c, pts: plus(A, c.rel) })) };
};

const tirerParams = (k, forme) => {
    const [a, b] = tirerCotes(k, true);
    let vecteur = [1, 1];
    for (let e = 0; e < 20; e++) { const v = [randInt(-2, 2), randInt(-2, 2)]; if (v[0] !== 0 || v[1] !== 0) { vecteur = v; break; } }
    return { k, forme, a, b, i: randInt(0, forme === 'rectangle' ? 3 : 2), sx: randomChoice([1, -1]), sy: randomChoice([1, -1]), axe: randInt(0, 1), vecteur };
};

export const tirerReconnaitre = () => {
    // Le rapport et la forme sont tirés UNE fois (sinon les rapports dont la grille est plus
    // difficile à remplir seraient sous-représentés) ; ensuite, jusqu'à quatre tirages de côtés,
    // de sommet gardé et d'orientation pour une grille à trois erreurs de support, avant
    // d'accepter le hors-support en remplacement.
    const k = randomChoice(RAPPORTS); const forme = randomChoice(FORMES);
    for (let essai = 0; essai < 400; essai++) {
        const t = reconnaitreTirage(tirerParams(k, forme));
        if (t && (essai % 4 === 3 || !t.candidats.some((c) => c.code === 'sommet_deplace'))) return t;
    }
    // Repli déterministe, vérifié par payloadReconnaitre : le rectangle 3 × 2 doublé depuis son coin bas gauche.
    return reconnaitreTirage({ k: RAPPORTS[0], forme: 'rectangle', a: 3, b: 2, i: 0, sx: 1, sy: 1, axe: 0, vecteur: [1, 1] }, (r) => [r.xLo, r.yLo]);
};

const reconnaitreValide = (t) => t && [3, 4].includes(t.sommets.length) && entiers(t.sommets) && dansGrille(t.sommets)
    && t.candidats.length === 4 && t.candidats[0].code === 'cle'
    && t.candidats.every((c) => c.pts.length === t.sommets.length && entiers(c.pts) && dansGrille(c.pts))
    && t.candidats.slice(1).every((c) => CODES.includes(c.code))
    && t.candidats.filter((c) => c.code === 'sommet_deplace').length <= 1
    && new Set([cleDe(t.sommets), ...t.candidats.map((c) => cleDe(c.pts))]).size === 5
    && cotesDroits(t.decor, t.sommets) && !estSacrifie(t.forme, t.a, t.b, t.k);

/** Les tracés d'une figure : polygone fermé, ou les deux segments du L. */
const traces = (decor, pts, couleur, dash) => (decor.length === 1
    ? { polys: [{ pts, couleur, dash, opacite: dash ? 0.15 : 0.08 }], segments: [] }
    : { polys: [], segments: segmentsDe(decor, pts).map(([p, q]) => ({ a: p, b: q, couleur, dash })) });

const CADRE = { xMin: 0, xMax: GMAX, yMin: 0, yMax: GMAX };

/** L'énoncé : la figure bleue nommée ; data-depart pour le solveur. */
const svgEnonce = (t) => {
    const tr = traces(t.decor, t.sommets, BLEU, false);
    return svgQuadrillage({
        ...CADRE, polys: tr.polys, segments: tr.segments,
        points: t.sommets.map((p, i) => ({ x: p[0], y: p[1], nom: NOMS[i], couleur: BLEU })),
        data: { depart: t.sommets.map((p, i) => `${NOMS[i]}:${p[0]};${p[1]}`).join('|') },
    });
};

/** Un dessin proposé : la figure bleue en clair, la figure orange construite ; data-figure pour le solveur. */
const svgPanneau = (t, pts) => {
    const bleu = traces(t.decor, t.sommets, BLEU, false);
    const orange = traces(t.decor, pts, ORANGE, true);
    return svgQuadrillage({
        ...CADRE, polys: [...bleu.polys, ...orange.polys], segments: [...bleu.segments, ...orange.segments],
        data: { figure: cleDe(pts) },
    });
};

/** Choix image d'un QCM : imageData obligatoire dans un choix (FORMATS §1, modèle gen_184). */
const choixImage = (svg) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } });

export const payloadReconnaitre = (t, ecriture) => {
    if (!reconnaitreValide(t)) throw new Error('gen_181 reconnaitre : tirage invalide');
    const noms = NOMS.slice(0, t.sommets.length);
    const nomFigure = noms.join('');
    const panneaux = t.candidats.map((c) => svgPanneau(t, c.pts));
    // Second chemin : relire data-figure de chaque dessin ; un seul égale A + k × (P − A) pour tous les sommets, et c'est le premier.
    const A = t.sommets[0];
    const attendu = cleDe(t.sommets.map((p) => [A[0] + ((p[0] - A[0]) * t.k.n) / t.k.d, A[1] + ((p[1] - A[1]) * t.k.n) / t.k.d]));
    const lus = panneaux.map((svg) => (svg.match(/ data-figure="([^"]+)"/) || [])[1]);
    if (lus[0] !== attendu || lus.filter((s) => s === attendu).length !== 1 || new Set(lus).size !== 4) throw new Error('gen_181 reconnaitre : dessins');
    const images = panneaux.map(choixImage);
    const question = ecriture === 'mots'
        ? `Sur quel dessin la figure orange est-elle la figure bleue dont toutes les longueurs ont été ${MOTS[kStr(t.k)]} ?`
        : `Sur quel dessin la figure orange est-elle ${rapportKatex(t.k)} de la figure bleue ?`;
    const payload = makeQcm(images, images[0],
        `<p>La figure bleue ${nomFigure} est dessinée sur le quadrillage. Sur chacun des quatre dessins, une figure orange a été construite.</p>`
        + `<p><b>${question}</b></p>`,
        { ...opts(L_RECON), elements_additionnels: imageEnonce(svgEnonce(t), `Quadrillage : la figure bleue ${nomFigure}.`, 'small') });
    payload._meta.params = {
        forme: t.forme, k: kStr(t.k), cotes: [t.a, t.b], sommetGarde: coinDe(t.sommets), ecriture, cle: attendu,
        distracteurs: t.candidats.slice(1).map((c) => ({
            code: c.code,
            ...(c.rapport ? { rapport: c.rapport } : {}),
            ...(c.axe != null ? { axe: c.axe === 0 ? 'horizontal' : 'vertical' } : {}),
            ...(c.vecteur ? { vecteur: c.vecteur } : {}),
        })),
    };
    return payload;
};

const genReconnaitre = () => payloadReconnaitre(tirerReconnaitre(), tirerEcriture());

// ═════════ Registre ═════════

const GENERATORS = {
    construire_placer_points_2d: genConstruire,
    reconnaitre_qcm_unique: genReconnaitre,
};

const CAT = [
    { id: 'construire', label: L_CONS, key: 'construire_placer_points_2d', fmt: 'Repère' },
    { id: 'reconnaitre', label: L_RECON, key: 'reconnaitre_qcm_unique', fmt: 'QCM' },
];

export const CATEGORIES = CAT.map((c) => ({ id: c.id, label: c.label, formatsList: [{ key: c.key, label: c.fmt }] }));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const m = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = m.length ? randomChoice(m) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { GENERATORS, tirerCotes, SACRIFIE, CODES };
